/**
 * Pure transforms for the Session Performance "Summary" tab.
 *
 * Reuses the session_games + elo_history data already loaded by
 * PlayerSessionPerformancePage — no extra queries for anything scoped to
 * *this* session. Ladder scope ('all' | 'mixed' | 'levels') is independent
 * from the SessionPerformanceHero toggle — see SESSION_LIFECYCLE_ARCHITECTURE.md §3.7.
 *
 * Elo math (expected score) is delegated to eloUtils.js — see
 * docs/elo/ELO_ALGORITHM.md for the formula itself.
 */
import { calculateExpectedScore } from './eloUtils';
import { affectsElo } from './gameTypeUtils';
import { getEloFromMap } from './sessionPerformanceGameHistory';

/**
 * Matches the existing hero/page ladder filter: untagged games count as mixed.
 * 'all' means no filtering — every game the player played this session.
 */
export function isGameInLadder(pairType, ladderView) {
  if (ladderView === 'all') return true;
  if (ladderView === 'levels') return pairType === 'levels';
  return !pairType || pairType === 'mixed';
}

function playerDisplayName(player) {
  if (!player) return 'Unknown';
  return `${player.first_name || ''} ${player.last_name || ''}`.trim() || 'Unknown';
}

/** Shared "Name & Name" formatting for opponents/teammates lists. */
export function formatOpponentNames(opponents) {
  if (!opponents?.length) return 'opponent';
  return opponents.map((o) => o.name).join(' & ');
}

/** Average of each player's pre-game (`old_value`) rating for this specific game. */
function averageOldValue(participants, eloMap, gameId) {
  const values = participants
    .map((p) => getEloFromMap(eloMap, gameId, p.player_id)?.old_value)
    .filter((v) => v != null);
  if (!values.length) return null;
  return values.reduce((sum, v) => sum + v, 0) / values.length;
}

/** True when a game should appear on the session Elo trend chart (rated, Elo-affecting). */
function isEloChartPoint(row) {
  if (row.newElo == null || row.eloChange == null || row.eloChange === 0) return false;
  if (row.gameTypeId != null && !affectsElo(row.gameTypeId)) return false;
  return true;
}

/** Elo series (chronological, per-ladder game numbering) for the trend chart. */
function buildEloSeries(rowsForSeries) {
  return rowsForSeries
    .filter(isEloChartPoint)
    .map((r, idx) => ({ gameNumber: idx + 1, newElo: r.newElo, delta: r.eloChange, result: r.result }));
}

/** "Started strong and faded" / "finished strong" / "consistent" narrative. */
function computeMomentum(series) {
  const deltas = series.map((r) => r.delta).filter((d) => d != null);
  if (deltas.length < 4) return null;
  const mid = Math.floor(deltas.length / 2);
  const avg = (arr) => arr.reduce((sum, v) => sum + v, 0) / arr.length;
  const diff = avg(deltas.slice(0, mid)) - avg(deltas.slice(mid));
  if (diff > 3) return 'strong_start_faded';
  if (diff < -3) return 'slow_start_finished_strong';
  return 'consistent';
}

/** First/last rating for one ladder's rows, independent of the active ladderView. */
function seriesEdgeRatings(rowsForLadder) {
  const withElo = rowsForLadder.filter((r) => r.myOldElo != null && r.newElo != null);
  if (!withElo.length) return null;
  const start = withElo[0].myOldElo;
  const end = withElo[withElo.length - 1].newElo;
  return { start, end, change: end - start };
}

function formatWinsClause(wins, total) {
  if (total === 0) return null;
  if (wins === total) return total === 1 ? 'won your game' : 'won all of your games';
  return `won ${wins} of ${total} games`;
}

const NUMBER_WORDS = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten'];
function numberWord(n) {
  return n >= 0 && n <= 10 ? NUMBER_WORDS[n] : String(n);
}

/** Ironically motivating shutout copy — separate pools per Summary ladder toggle. */
const SHUTOUT_MULTI_GAME_LINES = {
  mixed: [
    'Winless in mixed tonight — the partner draw resets next session.',
    (total) => `${total} mixed losses — statistically the shuttle owes you one.`,
    'Mixed was not kind today. Your next pairing is due a plot twist.',
    'Zero mixed wins — court karma is real and yours is loading.',
  ],
  levels: [
    (total, label) => `Winless on the ${label} court — next session gets a rematch.`,
    (total, label) => `${total} on the ${label} loss column — bounce-back incoming.`,
    (_total, label) => `${label} doubles was not your night. The court will still be there.`,
    (total, label) => `${total} losses in ${label} — the comeback write-up starts now.`,
  ],
  all: [
    (total) => `${total} games, zero wins — call it a long warm-up.`,
    'Winless across the session — the scoreboard does not get the last word.',
    (total) => `${total} on the loss column — statistically, you are due a bounce-back.`,
    'Tough session all round. Next time the whole board flips.',
  ],
};

const SHUTOUT_SINGLE_GAME_LINES = {
  mixed: "That mixed game did not go your way. You'll get another draw soon.",
  levels: (_total, label) => `That ${label} game did not go your way. You'll get another crack at it.`,
  all: "That game did not go your way. You'll have another chance next time.",
};

/** Stable index from a string — same session + player + ladder always gets the same line. */
function hashStringToIndex(key, length) {
  let hash = 0;
  for (let i = 0; i < key.length; i++) {
    hash = (Math.imul(31, hash) + key.charCodeAt(i)) | 0;
  }
  return Math.abs(hash) % length;
}

function resolveShutoutLine(entry, total, levelsLabel) {
  return typeof entry === 'function' ? entry(total, levelsLabel) : entry;
}

function pickShutoutMultiGameLine(total, seedKey, ladderView, levelsLabel) {
  const pool = SHUTOUT_MULTI_GAME_LINES[ladderView] ?? SHUTOUT_MULTI_GAME_LINES.all;
  const index = hashStringToIndex(seedKey, pool.length);
  return resolveShutoutLine(pool[index], total, levelsLabel);
}

function pickShutoutSingleGameLine(ladderView, levelsLabel) {
  const entry = SHUTOUT_SINGLE_GAME_LINES[ladderView] ?? SHUTOUT_SINGLE_GAME_LINES.all;
  return resolveShutoutLine(entry, 1, levelsLabel);
}

function formatExpectationVsActualClause(overperformance) {
  const winCount = Math.round(Math.abs(overperformance));
  if (winCount <= 0) return null;
  if (overperformance > 0) {
    return `won ${numberWord(winCount)} more than expected`;
  }
  return `lost ${numberWord(winCount)} more game${winCount === 1 ? '' : 's'} than expected`;
}

/** Builds the "Great session!" style narrative sentence — pure text, no new data. */
function buildNarrativeSummary({
  record,
  ratingChange,
  overperformance,
  biggestUpset,
  shutoutSeedKey,
  ladderView = 'mixed',
  levelsLabel = 'Levels',
}) {
  const total = record.wins + record.losses;
  if (total === 0) return null;

  if (record.wins === 0) {
    const line =
      total === 1
        ? pickShutoutSingleGameLine(ladderView, levelsLabel)
        : pickShutoutMultiGameLine(total, shutoutSeedKey ?? String(total), ladderView, levelsLabel);
    return line.endsWith('.') ? line : `${line}.`;
  }

  const winsClause = formatWinsClause(record.wins, total);
  let sentence = `You ${winsClause}`;

  if (ratingChange != null && Math.round(ratingChange) !== 0) {
    sentence += `, ${ratingChange > 0 ? 'gained' : 'lost'} ${Math.abs(Math.round(ratingChange))} rating`;
  }

  if (overperformance != null) {
    const expectationClause = formatExpectationVsActualClause(overperformance);
    if (expectationClause) {
      sentence += `, and ${expectationClause}`;
    }
  }

  sentence += '.';

  if (biggestUpset) {
    sentence += ` Your win against ${formatOpponentNames(biggestUpset.opponents)} was your biggest upset of the night!`;
  }

  return sentence;
}

const EMPTY_INSIGHTS = {
  hasGames: false,
  gamesPlayed: 0,
  record: { wins: 0, losses: 0, winPct: null },
  eloSeries: [],
  mixedEloSeries: [],
  levelsEloSeries: [],
  momentumLabel: null,
  narrativeSummary: null,
  scoring: { pointsFor: 0, pointsAgainst: 0, avgPointDiff: null },
  highlights: { toughestMatch: null, biggestWin: null },
  facts: {
    expectedWins: null,
    actualWins: 0,
    overperformance: null,
    biggestRatingGain: null,
    upsets: [],
    toughestOpponentBeat: null,
  },
  ratingBreakdown: { mixed: null, levels: null },
  opponentSessionRecords: [],
  avgOpponentTeamRating: null,
  gameTimeline: [],
};

/**
 * Builds Summary-tab stats for one player's games in one session, scoped to a ladder.
 *
 * @param {Array} sessionGames - raw session_games rows (players_session_games.players nested)
 * @param {Map} eloMap - from buildEloByGamePlayerMap, keyed `${session_game_id}:${player_id}`
 * @param {string} playerId
 * @param {'all'|'mixed'|'levels'} [ladderView='mixed']
 * @returns {object}
 */
export function buildSessionPlayerInsights(
  sessionGames,
  eloMap,
  playerId,
  ladderView = 'mixed',
  { levelsLabel = 'Levels' } = {},
) {
  if (!sessionGames?.length || !playerId) return EMPTY_INSIGHTS;

  // Collect every game the player played this session (no ladder filter yet) —
  // needed so ratingBreakdown/dual elo series can always see both ladders.
  const allRows = [];
  sessionGames.forEach((game) => {
    const gamePlayers = game.players_session_games || [];
    const playerGame = gamePlayers.find((pg) => pg.player_id === playerId);
    if (!playerGame) return;

    const myTeamNumber = playerGame.team_number;
    const teammates = gamePlayers.filter(
      (p) => p.team_number === myTeamNumber && p.player_id !== playerId,
    );
    const opponents = gamePlayers.filter((p) => p.team_number !== myTeamNumber);

    const myScore = myTeamNumber === 1 ? game.team_1_score : game.team_2_score;
    const oppScore = myTeamNumber === 1 ? game.team_2_score : game.team_1_score;
    const margin = myScore != null && oppScore != null ? myScore - oppScore : null;

    const myEloRow = getEloFromMap(eloMap, game.id, playerId);
    const myTeamAvgElo = averageOldValue([{ player_id: playerId }, ...teammates], eloMap, game.id);
    const oppTeamAvgElo = averageOldValue(opponents, eloMap, game.id);

    const expectedWinProb =
      myTeamAvgElo != null && oppTeamAvgElo != null
        ? calculateExpectedScore(myTeamAvgElo, oppTeamAvgElo)
        : null;

    allRows.push({
      gameId: game.id,
      createdAt: game.created_at,
      pairType: playerGame.pair_type,
      result: playerGame.result,
      myScore,
      oppScore,
      margin,
      opponents: opponents.map((o) => ({
        id: o.player_id,
        name: playerDisplayName(o.players),
        gender: o.players?.gender,
      })),
      teammates: teammates.map((t) => ({
        id: t.player_id,
        name: playerDisplayName(t.players),
        gender: t.players?.gender,
      })),
      myTeamAvgElo,
      oppTeamAvgElo,
      expectedWinProb,
      isUpset:
        playerGame.result === 'win' &&
        myTeamAvgElo != null &&
        oppTeamAvgElo != null &&
        myTeamAvgElo < oppTeamAvgElo,
      eloChange: myEloRow?.elo_change ?? null,
      myOldElo: myEloRow?.old_value ?? null,
      newElo: myEloRow?.new_value ?? null,
      gameTypeId: myEloRow?.game_type_id ?? null,
    });
  });

  if (!allRows.length) return EMPTY_INSIGHTS;

  allRows.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));

  const rows = allRows.filter((r) => isGameInLadder(r.pairType, ladderView));
  if (!rows.length) return EMPTY_INSIGHTS;

  // ── Always-both-ladders rows, independent of the active toggle ──────────
  const mixedRows = allRows.filter((r) => !r.pairType || r.pairType === 'mixed');
  const levelsRows = allRows.filter((r) => r.pairType === 'levels');

  // ── Elo series + momentum narrative ──────────────────────────────────────
  const mixedEloSeries = buildEloSeries(mixedRows);
  const levelsEloSeries = buildEloSeries(levelsRows);
  // Combining mixed + levels into one line is meaningless (different rating pools) —
  // the 'all' view relies on mixedEloSeries/levelsEloSeries for a dual-line chart instead.
  const eloSeries = ladderView === 'all' ? [] : buildEloSeries(rows);

  let momentumLabel = null;
  if (ladderView === 'all') {
    const mixedHas = mixedEloSeries.length > 0;
    const levelsHas = levelsEloSeries.length > 0;
    if (mixedHas && !levelsHas) momentumLabel = computeMomentum(mixedEloSeries);
    else if (levelsHas && !mixedHas) momentumLabel = computeMomentum(levelsEloSeries);
  } else {
    momentumLabel = computeMomentum(eloSeries);
  }

  // ── Win/loss record ───────────────────────────────────────────────────────
  const wins = rows.filter((r) => r.result === 'win').length;
  const losses = rows.filter((r) => r.result === 'loss').length;
  const totalDecided = wins + losses;
  const record = { wins, losses, winPct: totalDecided ? (wins / totalDecided) * 100 : null };

  // ── Scoring KPIs ──────────────────────────────────────────────────────────
  const scoredRows = rows.filter((r) => r.myScore != null && r.oppScore != null);
  const pointsFor = scoredRows.reduce((sum, r) => sum + r.myScore, 0);
  const pointsAgainst = scoredRows.reduce((sum, r) => sum + r.oppScore, 0);
  const avgPointDiff = scoredRows.length ? (pointsFor - pointsAgainst) / scoredRows.length : null;

  // ── Highlights ────────────────────────────────────────────────────────────
  let toughestMatch = null;
  let biggestWin = null;
  scoredRows.forEach((r) => {
    if (!toughestMatch || Math.abs(r.margin) < Math.abs(toughestMatch.margin)) {
      toughestMatch = r;
    }
    if (r.result === 'win' && (!biggestWin || r.margin > biggestWin.margin)) {
      biggestWin = r;
    }
  });

  // ── Interesting facts ─────────────────────────────────────────────────────
  const ratedRows = rows.filter((r) => r.expectedWinProb != null);
  const expectedWins = ratedRows.length
    ? ratedRows.reduce((sum, r) => sum + r.expectedWinProb, 0)
    : null;
  const actualWins = ratedRows.filter((r) => r.result === 'win').length;
  const overperformance = expectedWins != null ? actualWins - expectedWins : null;

  let biggestRatingGain = null;
  rows.forEach((r) => {
    if (r.eloChange == null) return;
    if (!biggestRatingGain || r.eloChange > biggestRatingGain.eloChange) {
      biggestRatingGain = r;
    }
  });

  const upsets = rows.filter((r) => r.isUpset);

  let biggestUpset = null;
  upsets.forEach((u) => {
    const gap = (u.oppTeamAvgElo ?? 0) - (u.myTeamAvgElo ?? 0);
    if (!biggestUpset || gap > biggestUpset.gap) biggestUpset = { ...u, gap };
  });

  let toughestOpponentBeat = null;
  rows.forEach((r) => {
    if (r.result !== 'win' || r.oppTeamAvgElo == null) return;
    if (!toughestOpponentBeat || r.oppTeamAvgElo > toughestOpponentBeat.oppTeamAvgElo) {
      toughestOpponentBeat = r;
    }
  });

  // ── Rating breakdown (own rating, both ladders, independent of toggle) ───
  const ratingBreakdown = {
    mixed: seriesEdgeRatings(mixedRows),
    levels: seriesEdgeRatings(levelsRows),
  };

  // ── Narrative headline ────────────────────────────────────────────────────
  const ratedFilteredRows = rows.filter((r) => r.eloChange != null);
  const ratingChange = ratedFilteredRows.length
    ? ratedFilteredRows.reduce((sum, r) => sum + r.eloChange, 0)
    : null;
  const shutoutSeedKey = `${playerId}:${ladderView}:${rows.map((r) => r.gameId).sort().join(',')}`;
  const narrativeSummary = buildNarrativeSummary({
    record,
    ratingChange,
    overperformance,
    biggestUpset,
    shutoutSeedKey,
    ladderView,
    levelsLabel,
  });

  // ── Opponents faced this session (individual, aggregated across games) ───
  // Note: current ratings aren't available here — they live in
  // player_club_elo_ratings (club+season scoped), not on the session_games
  // payload. `lastPairType` is exposed so the caller can pick mixed vs. levels
  // when it separately fetches those ratings (see fetchOpponentCurrentRatings).
  const opponentMap = new Map();
  rows.forEach((r) => {
    r.opponents.forEach((opp) => {
      if (!opp.id) return;
      if (!opponentMap.has(opp.id)) {
        opponentMap.set(opp.id, {
          opponentId: opp.id,
          name: opp.name,
          gender: opp.gender,
          wins: 0,
          losses: 0,
          pointsFor: 0,
          pointsAgainst: 0,
          lastPairType: null,
          lastPlayedAt: null,
        });
      }
      const entry = opponentMap.get(opp.id);
      if (r.result === 'win') entry.wins += 1;
      else if (r.result === 'loss') entry.losses += 1;
      if (r.myScore != null) entry.pointsFor += r.myScore;
      if (r.oppScore != null) entry.pointsAgainst += r.oppScore;
      if (!entry.lastPlayedAt || new Date(r.createdAt) >= new Date(entry.lastPlayedAt)) {
        entry.lastPairType = r.pairType;
        entry.lastPlayedAt = r.createdAt;
      }
    });
  });
  const opponentSessionRecords = Array.from(opponentMap.values())
    .map((entry) => {
      const games = entry.wins + entry.losses;
      const avgOppPointDiff = games ? (entry.pointsFor - entry.pointsAgainst) / games : null;
      return {
        opponentId: entry.opponentId,
        name: entry.name,
        gender: entry.gender,
        wins: entry.wins,
        losses: entry.losses,
        games,
        pointsFor: entry.pointsFor,
        pointsAgainst: entry.pointsAgainst,
        avgPointDiff: avgOppPointDiff,
        lastPairType: entry.lastPairType,
      };
    })
    .sort((a, b) => b.games - a.games || a.name.localeCompare(b.name));

  const ratingsForAvgOpp = rows.map((r) => r.oppTeamAvgElo).filter((v) => v != null);
  const avgOpponentTeamRating = ratingsForAvgOpp.length
    ? ratingsForAvgOpp.reduce((sum, v) => sum + v, 0) / ratingsForAvgOpp.length
    : null;

  // ── Session timeline (chronological, matches active ladder toggle) ──────
  const gameTimeline = rows.map((r, idx) => ({
    gameNumber: idx + 1,
    gameId: r.gameId,
    createdAt: r.createdAt,
    pairType: r.pairType,
    result: r.result,
    myScore: r.myScore,
    oppScore: r.oppScore,
    opponents: r.opponents,
    teammates: r.teammates,
    eloChange: r.eloChange,
    myOldElo: r.myOldElo,
    newElo: r.newElo,
  }));

  return {
    hasGames: true,
    gamesPlayed: rows.length,
    record,
    eloSeries,
    mixedEloSeries,
    levelsEloSeries,
    momentumLabel,
    narrativeSummary,
    scoring: { pointsFor, pointsAgainst, avgPointDiff },
    highlights: { toughestMatch, biggestWin },
    facts: { expectedWins, actualWins, overperformance, biggestRatingGain, upsets, toughestOpponentBeat },
    ratingBreakdown,
    opponentSessionRecords,
    avgOpponentTeamRating,
    gameTimeline,
  };
}
